import {
  ActionVisibilityValueType,
  questionnaireLogicService,
} from 'utils/adl-services';
import { isMatrixSingleOrMultipleNode } from 'utils/nodeTypes';

function isMatchingCarriedForward(item, refId) {
  return (
    item.isCarriedForward &&
    (item.originalAnswerRefId === refId ||
      item.originalScorepointRefId === refId)
  );
}

// The item itself first, then every carried-forward copy of it, at any depth of
// the carry forward chain: an answer or a scorepoint, carried forward as an
// answer or as a scorepoint.
export function getCarriedForwardedOptionsByRefId({ nodes, initialObject }) {
  const queue = [initialObject];
  const results = [initialObject];

  while (queue.length > 0) {
    const { nodeRefId, valueRefId } = queue.shift();

    nodes.forEach((node) => {
      if (!node.carryForward || node.carryForward.length === 0) {
        return;
      }

      const isMatchingCarryNode = node.carryForward.find(
        ({ questionRefId }) => questionRefId === nodeRefId,
      );

      if (!isMatchingCarryNode) {
        return;
      }

      if (node?.answers) {
        node.answers.forEach((answer) => {
          const isSingleOrMultipleMatrix = isMatrixSingleOrMultipleNode(node);

          if (isMatchingCarriedForward(answer, valueRefId)) {
            const matchedAnswer = {
              nodeRefId: node.refId,
              valueRefId: answer.refId,
              valueType: isSingleOrMultipleMatrix
                ? ActionVisibilityValueType.Row
                : ActionVisibilityValueType.Answer,
            };
            queue.push(matchedAnswer);
            results.push(matchedAnswer);
          }
        });
      }

      if (node?.scorepoints) {
        node.scorepoints.forEach((scorepoint) => {
          if (isMatchingCarriedForward(scorepoint, valueRefId)) {
            const matchedScorepoint = {
              nodeRefId: node.refId,
              valueRefId: scorepoint.refId,
              valueType: ActionVisibilityValueType.Column,
            };
            queue.push(matchedScorepoint);
            results.push(matchedScorepoint);
          }
        });
      }

      if (node?.matrix?.matrixAnswers) {
        node.matrix.matrixAnswers.forEach((row) => {
          if (isMatchingCarriedForward(row, valueRefId)) {
            const matchedRow = {
              nodeRefId: node.refId,
              valueRefId: row.refId,
              valueType: ActionVisibilityValueType.Row,
            };
            queue.push(matchedRow);
            results.push(matchedRow);
          }
        });
      }

      if (node?.matrix?.columns) {
        node.matrix.columns.forEach((column) => {
          if (isMatchingCarriedForward(column, valueRefId)) {
            const matchedColumn = {
              nodeRefId: node.refId,
              valueRefId: column.refId,
              valueType: ActionVisibilityValueType.Column,
            };
            queue.push(matchedColumn);
            results.push(matchedColumn);
          }
        });
      }
    });
  }

  return results;
}

// Removes the logic on the item and on every carried-forward copy of it.
// `logicNodeRefId` is the node holding that logic when it is not each item's
// own, as for disqualification logic.
export function qlRemoveLogicOfItemAndCopies({
  ql,
  nodes,
  nodeRefId,
  valueRefId,
  valueType = ActionVisibilityValueType.Answer,
  logicNodeRefId,
  updateQuestionnaireNode = true,
}) {
  return getCarriedForwardedOptionsByRefId({
    nodes,
    initialObject: { nodeRefId, valueRefId, valueType },
  }).reduce((newQl, item) => {
    const isColumnType = item.valueType === ActionVisibilityValueType.Column;

    return questionnaireLogicService.removeAnswerWithLogic(
      newQl,
      logicNodeRefId || item.nodeRefId,
      isColumnType ? null : item.valueRefId,
      isColumnType ? item.valueRefId : null,
      updateQuestionnaireNode,
    );
  }, ql);
}
